import { useState, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useSuspenseQuery } from '@tanstack/react-query'

import { logWarning, setRumContextProperty } from '@/services/errorService'

import { getLocation, type LocationData } from '@/shared/api/location'
import { COUNTRY_TO_SUPPORTED_LOCALE } from '@/constants/localeConstants'
import { useLocale } from '@/providers/I18nProvider'
import { getTimeSlots, type TimeSlotsResponse } from '@/shared/api/timeSlots'
import { initializeLDClient } from '@/lib/launchDarkly'
import { initMetaPixel } from '@/lib/metaPixel'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import { PATHS } from '@/router/paths'
import { useLocationRef } from '@/contexts/RefContext'
import { useAppDispatch } from '@/store'
import {
	restoreCart,
	setReferral,
	setRwgToken,
	setPendingReorder,
	setDineInAllowDuringOffHours,
	setNonDineInOrderTypeCount,
	setHasFutureSlotsForTakeout,
	setHasFutureSlotsForDelivery,
	setTipSettings,
	setTenders,
} from '@/store/ticketSlice'
import { setReferral as persistReferral, type ReferralSource } from '@/lib/referralStorage'

function isKnownReferralSource(s: string): s is ReferralSource {
	return s === 'skytabwebsite' || s === 'owg'
}

// Reads the stored orderTypeGuid from localStorage for the given location ref.
// Both the dine-in detection (render-time) and the same-day closure check (async effect)
// navigate the same record.orderTypeGuid ?? settings.orderTypeGuid path — extracted here
// to eliminate the duplication flagged in TL review (STO-3386).
function readStoredOrderTypeRef(ref: string): string | null {
	try {
		const raw = localStorage.getItem(ref)
		if (!raw) return null
		const parsed: unknown = JSON.parse(raw)
		if (typeof parsed !== 'object' || parsed === null) return null
		const record = parsed as Record<string, unknown>
		const settings =
			typeof record.orderSettings === 'object' && record.orderSettings !== null
				? (record.orderSettings as Record<string, unknown>)
				: record
		const guid = record.orderTypeGuid ?? settings.orderTypeGuid
		return typeof guid === 'string' ? guid : null
	} catch {
		return null
	}
}

type LDIdentificationState = 'idle' | 'identifying' | 'ready' | 'failed'

type LocationBootstrapResult =
	| { location: null; isReady: false; ldIdentificationState: LDIdentificationState }
	| {
			location: LocationData
			isReady: true
			ldIdentificationState: LDIdentificationState
			isStoreClosureActive: boolean
			activeStoreClosureMessage: string | null
			hasSameDayClosure: boolean
	  }

export function useLocationBootstrap(): LocationBootstrapResult {
	const ref = useLocationRef()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [searchParams] = useSearchParams()
	const { isUserOverridden, changeLocale } = useLocale()
	const [isReady, setIsReady] = useState(false)
	const [ldIdentificationState, setLdIdentificationState] =
		useState<LDIdentificationState>('idle')
	const [isStoreClosureActive, setIsStoreClosureActive] = useState(false)
	const [activeStoreClosureMessage, setActiveStoreClosureMessage] = useState<string | null>(null)
	const [hasSameDayClosure, setHasSameDayClosure] = useState(false)

	// Derived at hook level so useSuspenseQuery can include isDineIn in the request.
	// Sanitize matching StartOrder.tsx (OWASP A03) — injection chars must not produce
	// a false-positive dine-in detection before the query fires.
	const rawTable = searchParams.get('tableName') ?? searchParams.get('tableNotes')
	const sanitizedTable = rawTable
		? rawTable
				.replace(/[<>"'`]/g, '')
				.trim()
				.slice(0, 64) || null
		: null
	const isDineInScan = !!sanitizedTable || searchParams.get('dineIn') === 'true'

	// Peek at localStorage before the query fires so getLocation is called with
	// ?dineIn=true when a stored DINE_IN session exists (e.g. hard refresh on checkout).
	// Without this, getLocation(ref, false) omits DINE_IN from orderTypes and restoreCart
	// can never match the stored guid.
	//
	// sessionStorage is cleared when a tab closes but survives F5 within the same tab.
	// The dineIn_active flag written in bootstrap() during an active dine-in scan is used
	// here to distinguish a same-tab hard refresh (flag present → restore session) from a
	// fresh navigation after closing the dine-in tab (flag absent → discard stale session).
	// When the flag is absent the stored entry is removed so restoreCart sees nothing and
	// the Order Settings modal is shown instead of silently restoring the dine-in flow.
	const storedIsDineIn: boolean = (() => {
		if (readStoredOrderTypeRef(ref) !== 'DINE_IN') return false
		try {
			if (sessionStorage.getItem(`dineIn_active_${ref}`) !== null) return true
			// Note: if the customer has two tabs open simultaneously — one dine-in, one regular —
			// the regular tab's IIFE will call removeItem() and silently wipe the dine-in tab's
			// session data. The dine-in tab's sessionStorage flag survives but its localStorage
			// entry will be gone on the next bootstrap. This is a known limitation of the
			// localStorage-per-location-ref architecture (same behaviour as Ember). It is not
			// addressed here — the 2-hour TTL bounds the window of exposure.
			localStorage.removeItem(ref)
			return false
		} catch {
			// sessionStorage.getItem threw (storage blocked or unavailable).
			// Cannot distinguish same-tab refresh from new tab — default to preserving the
			// session so a legitimate F5 refresh on checkout does not lose the dine-in state.
			// The 2-hour TTL in effectiveIsDineIn (bootstrap()) provides the expiry bound.
			return true
		}
	})()

	// When the user navigates directly to order-settings without a dineIn scan URL param,
	// they intend to choose or change their order type — do not silently restore a stored
	// dine-in session that would trigger the OrderSettingsPage DINE_IN guard and bounce
	// them to the menu. isDineInScan still takes precedence (explicit ?dineIn=true wins).
	const isDineIn =
		isDineInScan || (pathname === PATHS.orderSettings(ref) ? false : storedIsDineIn)

	const { data: location } = useSuspenseQuery({
		queryKey: ['location', ref, isDineIn],
		queryFn: () => getLocation(ref, isDineIn),
		staleTime: QUERY_STALE_TIMES.SHORT,
	})

	useEffect(() => {
		let cancelled = false

		async function bootstrap() {
			setIsReady(false)
			setLdIdentificationState('idle')
			setIsStoreClosureActive(false)
			setActiveStoreClosureMessage(null)
			setHasSameDayClosure(false)

			if (isDineInScan) {
				try {
					sessionStorage.setItem(`dineIn_active_${ref}`, '1')
				} catch {
					// sessionStorage unavailable (e.g. private browsing with storage blocked)
				}
			}

			// V1 parity: app/routes/location.js:134 — set browser tab title to location name on load.
			// Placed before isClosed guard so closed locations also get the location name in the title.
			document.title = `${location.name} - Online Ordering`

			// STO-3809: merchant-driven locale — if the user has not explicitly chosen a locale
			// via ?lang=, switch to the locale that matches the merchant's country code.
			// Explicit user choice (?lang=) always takes precedence (isUserOverridden === true).
			if (!isUserOverridden) {
				const merchantLocale = COUNTRY_TO_SUPPORTED_LOCALE[location.countryCode]
				if (merchantLocale) {
					changeLocale(merchantLocale)
				}
			}

			// Dispatch flag and settings before isClosed check so they're always available
			// in Redux, even if the location is permanently closed.
			dispatch(setTipSettings(location.tipSettings))
			dispatch(setTenders(location.tenders))
			dispatch(setDineInAllowDuringOffHours(location.dineInAllowDuringOffHours))
			dispatch(
				setNonDineInOrderTypeCount(
					location.orderTypes.filter((ot) => ot.ref !== 'DINE_IN').length,
				),
			)
			// Reset to null (unknown) so a stale value from a previous location is never carried
			// over. null is not "no future slots" — the fetch below only replaces it with a
			// boolean for order types whose time-slots request actually succeeded.
			dispatch(setHasFutureSlotsForTakeout(null))
			dispatch(setHasFutureSlotsForDelivery(null))
			setRumContextProperty('location_id', location.locationRef)

			// Identify the location context before any feature-flag routing decisions are made.
			// Called before the isClosed guard so both open and permanently-closed locations
			// always evaluate flags against the location context, not the anonymous context.
			setLdIdentificationState('identifying')
			try {
				await initializeLDClient(location)
				if (!cancelled) setLdIdentificationState('ready')
			} catch (error) {
				logWarning(
					'[useLocationBootstrap] LaunchDarkly init failed, flags will use defaults',
					{
						locationRef: ref,
						error,
					},
				)
				if (!cancelled) setLdIdentificationState('failed')
			}
			if (cancelled) return

			// initMetaPixel is intentionally skipped for closed locations — no orders can be
			// placed, so AddToCart/Purchase events will never fire. V1 fired a PageView here
			// because pixel init ran before the closure check; omitting it in V2 is correct.
			if (location.isClosed) {
				setIsReady(true)
				return
			}

			if (location.metaPixelId) {
				initMetaPixel(location.metaPixelId)
			}

			const nonDineInOrderTypes = location.orderTypes.filter((ot) => ot.ref !== 'DINE_IN')
			const dineInOrderType =
				location.orderTypes.find((ot) => ot.ref === 'DINE_IN') ??
				location.orderTypes[0] ??
				null

			// For dine-in scans check only the dine-in type. For regular orders check every
			// eligible non-dine-in type — the Store Closed overlay only fires when ALL types
			// are unavailable, so one type with no slots never blocks another that has them.
			const typesToCheck = isDineIn
				? dineInOrderType
					? [dineInOrderType]
					: []
				: nonDineInOrderTypes

			if (typesToCheck.length > 0) {
				const slotResults = await Promise.all(
					typesToCheck.map(async (ot) => {
						try {
							return { data: await getTimeSlots(ref, ot.ref), orderTypeRef: ot.ref }
						} catch (error) {
							logWarning(
								'[useLocationBootstrap] Time-slots fetch failed, skipping dynamic closure check',
								{ locationRef: ref, orderTypeRef: ot.ref, error },
							)
							return { data: null, orderTypeRef: ot.ref }
						}
					}),
				)
				if (cancelled) return

				// Require all fetches to succeed before deriving closure state. If any order
				// type's request failed its availability is unknown — activating the overlay on
				// the partial result set would incorrectly block ordering for that type.
				const fetchedResults = slotResults.filter(
					(r): r is { data: TimeSlotsResponse; orderTypeRef: string } => r.data !== null,
				)

				if (fetchedResults.length === typesToCheck.length) {
					// Active store closure — only show the overlay when EVERY order type is both
					// (a) affected by a same-day closure and (b) has no future slots to fall back to.
					// One reachable type (not covered by the closure, or with future availability)
					// keeps the app open so the user can still pick an unaffected type or a future time.
					const closureStatus = fetchedResults.map((result) => {
						// Fall back to today when no ASAP slot exists so historical or future
						// closures in the API response are never treated as same-day.
						const orderDate = new Date(
							result.data.asapTimeSlot?.orderTime ?? Date.now(),
						)
						const relevantClosures = result.data.storeClosures.filter((closure) => {
							const closureDate = new Date(closure.date)
							return (
								closureDate.getUTCFullYear() === orderDate.getUTCFullYear() &&
								closureDate.getUTCMonth() === orderDate.getUTCMonth() &&
								closureDate.getUTCDate() === orderDate.getUTCDate()
							)
						})
						return {
							orderTypeRef: result.orderTypeRef,
							hasClosure: relevantClosures.length > 0,
							// hasBlockedAsap: ASAP is actively unavailable right now.
							// Mirrors Ember menu.js: asapTimeSlot.isAvailable === false → ASAP is blocked.
							// Deliberately does NOT gate on relevantClosures.length > 0: ASAP can become
							// unavailable via a Marketplace config toggle with no closure record, and that
							// must also redirect the customer to Order Settings (STO-3386 follow-up).
							// A past same-day closure still appears in storeClosures but asapTimeSlot.isAvailable
							// is true — only hasBlockedAsap drives the Order Settings redirect.
							hasBlockedAsap:
								result.data.asapTimeSlot == null ||
								result.data.asapTimeSlot.isAvailable === false,
							message: relevantClosures[0]?.message ?? null,
							hasFutureSlots: result.data.futureTimeSlots.length > 0,
							// A closure spanning the whole slot horizon still returns future
							// slots, all marked isAvailable: false. Only available slots keep
							// the store "open" for the allClosed check — mirrors the
							// isAvailable filter OrderSettingsModal applies to its tiles, so
							// bootstrap never routes the user into a modal with zero types.
							hasAvailableFutureSlots: result.data.futureTimeSlots.some(
								(s) => s.isAvailable,
							),
						}
					})

					// Use isDineInScan for bypass, not isDineIn: the closure bypass should only apply
					// to active dine-in scans, not stored sessions that may have expired.
					// If a stored session expires, it won't be restored anyway (restoreCart checks
					// expiry), so don't skip the closure overlay — it may be legitimately blocking orders.
					//
					// STO-3775: deliberately NOT gated on location.dineInAllowDuringOffHours. In Ember
					// that flag gates only the checkout button (checkout-button.js:10) — it never
					// suppresses the menu. Requiring it here blocked dine-in customers behind the
					// full-screen Store Closed overlay at venues with the flag off, where Ember shows
					// an inline off-hours warning and keeps the menu browsable (category-tabs.js:57).
					// Checkout remains correctly gated by selectIsScheduleAvailable, which still
					// honours dineInAllowDuringOffHours.
					const shouldBypassEmptySlotClosureForDineIn = isDineInScan

					// allClosed: every order type has ASAP actively blocked AND no AVAILABLE
					// future slots. Uses hasBlockedAsap so a past closure that has ended
					// (asapTimeSlot.isAvailable: true) does not trigger the Store Closed overlay
					// when ASAP is actually available. Uses hasAvailableFutureSlots (not raw slot
					// presence) so closures covering every slot show the overlay instead of an
					// Order Settings modal with no selectable order type.
					// Dine-in off-hours bypass applies only when no actual closure record exists —
					// a real closure event (hasClosure) takes precedence and always shows the overlay.
					const allClosed = closureStatus.every(
						(s) => s.hasBlockedAsap && !s.hasAvailableFutureSlots,
					)
					const hasAnyClosure = closureStatus.some((s) => s.hasClosure)
					if (allClosed && (!shouldBypassEmptySlotClosureForDineIn || hasAnyClosure)) {
						setIsStoreClosureActive(true)
						const firstWithMessage = closureStatus.find((s) => s.message !== null)
						setActiveStoreClosureMessage(firstWithMessage?.message ?? null)
					}

					// Redirect to Order Settings only when the user's RESTORED order type has a
					// same-day closure actively blocking ASAP. Checking all types would redirect
					// a user with Pickup selected even when only Delivery is closed — wrong.
					// Peek at localStorage to find the stored orderTypeGuid before restoreCart runs.
					const storedOrderTypeRef = readStoredOrderTypeRef(ref)

					// Mirrors Ember's menu.js getSchedules() redirect when asapTimeSlot.isAvailable === false.
					// Skip for dine-in off-hours scans: the bypass already signals that no ordering
					// restriction applies, so setting hasSameDayClosure would be semantically wrong
					// and could mislead future consumers of this flag.
					if (
						!shouldBypassEmptySlotClosureForDineIn &&
						closureStatus.some(
							(s) =>
								s.hasBlockedAsap &&
								(s.hasFutureSlots || s.hasClosure) &&
								(storedOrderTypeRef === null ||
									s.orderTypeRef === storedOrderTypeRef),
						)
					) {
						setHasSameDayClosure(true)
					}

					// No active schedule — only set closure when every successfully-fetched type
					// has no ASAP slot and no future slots at all. Using slot absence (== null / length === 0)
					// rather than isAvailable flags avoids false positives when slots exist but are
					// temporarily outside their availability window.
					// Note: when allClosed is true, allEmpty is also true (no slots is implied by
					// allClosed). setIsStoreClosureActive is intentionally called in both blocks so
					// each path is self-contained — do not add an `else` here.
					const allEmpty = fetchedResults.every(
						(r) => r.data.asapTimeSlot == null && r.data.futureTimeSlots.length === 0,
					)
					// hasAnyClosure mirrors the allClosed guard above: a real closure record always
					// wins over the dine-in bypass. Without it, widening the bypass (STO-3775) would
					// let a genuinely closed store render as browsable for dine-in scans.
					if (allEmpty && (!shouldBypassEmptySlotClosureForDineIn || hasAnyClosure)) {
						setIsStoreClosureActive(true)
						const firstWithMessage = closureStatus.find((s) => s.message !== null)
						setActiveStoreClosureMessage(firstWithMessage?.message ?? null)
					}
				}

				// Only dispatch a boolean when the order type's fetch succeeded — fetchedResults
				// excludes rejected requests, so an absent entry means availability is unknown and
				// must stay null. Collapsing a failed fetch to false would let the cart hide the
				// Order Settings edit action as though the API had confirmed no future slots.
				const takeoutSlotResult = fetchedResults.find((r) => r.orderTypeRef === 'TAKEOUT')
				if (takeoutSlotResult) {
					dispatch(
						setHasFutureSlotsForTakeout(
							takeoutSlotResult.data.futureTimeSlots.length > 0,
						),
					)
				}

				const deliverySlotResult = fetchedResults.find((r) => r.orderTypeRef === 'DELIVERY')
				if (deliverySlotResult) {
					dispatch(
						setHasFutureSlotsForDelivery(
							deliverySlotResult.data.futureTimeSlots.length > 0,
						),
					)
				}
			}

			const referralGuid = searchParams.get('referralGuid')
			const referralSource = searchParams.get('referralSource')
			const rwgToken = searchParams.get('rwg_token')
			const itemGuid = searchParams.get('itemGuid')
			const itemRef = searchParams.get('itemRef')

			const paramsToStrip: string[] = []
			const MAX_GUID_LEN = 36
			const MAX_SOURCE_LEN = 64
			const MAX_RWG_TOKEN_LEN = 2048

			if (
				referralGuid &&
				referralSource &&
				referralGuid.length <= MAX_GUID_LEN &&
				referralSource.length <= MAX_SOURCE_LEN &&
				isKnownReferralSource(referralSource)
			) {
				persistReferral(ref, referralSource, referralGuid)
				dispatch(setReferral({ guid: referralGuid, source: referralSource }))
				paramsToStrip.push('referralGuid', 'referralSource')
			}

			if (rwgToken && rwgToken.length <= MAX_RWG_TOKEN_LEN) {
				persistReferral(ref, 'owg', rwgToken)
				dispatch(setRwgToken(rwgToken))
				paramsToStrip.push('rwg_token')
			}

			if (itemGuid && itemRef) {
				dispatch(setPendingReorder({ itemGuid, itemRef }))
			}

			if (paramsToStrip.length > 0) {
				const next = new URLSearchParams(searchParams)
				paramsToStrip.forEach((p) => next.delete(p))
				const qs = next.toString()
				void navigate(`${pathname}${qs ? `?${qs}` : ''}`, { replace: true })
			}

			if (cancelled) return

			// isDineIn already reflects the stored session AND the /order-settings suppression
			// (computed at render time), but the render-time peek skips the expiry check to
			// avoid Date.now() in render. Re-check expiry here (Date.now() is safe inside an
			// async effect) so an expired stored entry does not restore an active dine-in session.
			//
			// When isDineIn=false due to the /order-settings suppression, the early-exit below
			// means restoreCart runs with isDineIn=false — intentional. The user is navigating
			// to order-settings to choose or change their order type; restoring a stored dine-in
			// cart here would contradict that intent.
			const effectiveIsDineIn: boolean = (() => {
				if (isDineInScan) return true
				if (!isDineIn) return false
				try {
					const raw = localStorage.getItem(ref)
					if (raw === null) return false
					const parsed: unknown = JSON.parse(raw)
					if (typeof parsed !== 'object' || parsed === null) return false
					const record = parsed as Record<string, unknown>
					const settings =
						typeof record.orderSettings === 'object' && record.orderSettings !== null
							? (record.orderSettings as Record<string, unknown>)
							: record
					const expiresAt = record.expiresAt ?? settings.expiresAt
					if (typeof expiresAt === 'number' && expiresAt < Date.now()) return false
					return true
				} catch {
					return false
				}
			})()
			await dispatch(restoreCart({ ref, location, isDineIn: effectiveIsDineIn }))
			if (cancelled) return

			setIsReady(true)
		}

		void bootstrap()

		return () => {
			document.title = 'Online Ordering'
			cancelled = true
			// Deliberately no ldClient.close() here (STO-3829). The LD client is an
			// app-lifetime singleton and close() is irreversible — a closed client ignores
			// all future identify() calls, freezing every flag on the last-identified
			// context. Closing it on unmount meant navigating profile → menu remounted
			// this hook with a dead client, so ew-olo-v2-enabled evaluated with the
			// anonymous context (false) and V2NotEnabled rendered instead of the menu.
			// On ref change the next bootstrap's initializeLDClient() re-identifies.
		}
	}, [ref, location]) // eslint-disable-line react-hooks/exhaustive-deps -- dispatch/navigate/searchParams/pathname/isDineInScan are stable refs; changeLocale is useCallback([]) (stable identity); isUserOverridden is set once at mount from ?lang= param and never changes mid-session; all intentionally excluded

	if (!isReady) return { location: null, isReady: false, ldIdentificationState }
	return {
		location,
		isReady: true,
		ldIdentificationState,
		isStoreClosureActive,
		activeStoreClosureMessage,
		hasSameDayClosure,
	}
}
